<template>
  <div style="margin-top:20px">
    <div style="width: 100%; background-color: #eee; border: 1px dotted grey; margin-bottom: 25px;">
      <span style="font-size: 26px; font-weight: bold">Settings History</span><br/>
      <span style="font-style: italic; font-size: 18px">History of settings updates across every tenant in the system.</span>
    </div>

    <div v-if="items" class="settings-history-filters-row">
      <span class="admin-users-count">{{items.length}} of {{items_full.length}}</span>
      <b-form-select size="sm" v-model="tenant_filter" :options="tenantOptions" class="admin-users-select"/>
      <b-form-select size="sm" v-model="tenant_type_filter" :options="tenantTypeOptions" class="admin-users-select"/>
      <b-form-select size="sm" v-model="telematics_type_filter" :options="telematicsTypeOptions" class="admin-users-select"/>
      <span class="filters-divider"></span>
      <b-button-group size="sm">
        <b-button
          v-for="opt in quickRangeOptions"
          :key="opt.value"
          :pressed="filter_mode === 'quick' && quick_range === opt.value"
          variant="outline-secondary"
          v-on:click.prevent="apply_quick_range(opt.value)"
        >{{ opt.label }}</b-button>
      </b-button-group>
      <span class="admin-users-range-sep">or</span>
      <span class="admin-users-explicit-range">
        <date-picker style="width:120px" v-model="from_date" :config="dateOptions" :placeholder="from_date"/>
        &nbsp;to&nbsp;
        <date-picker style="width:120px" v-model="to_date" :config="dateOptions" :placeholder="to_date"/>
      </span>
      <b-button size="sm" v-on:click.prevent="apply_explicit_range()">Search</b-button>
    </div>

    <div class="settings-history-chips-row">
      <template v-if="topTenants.length">
        <span class="chip-group-label">Tenants:</span>
        <button
          v-for="t in topTenants" :key="'tenant-' + t.value"
          type="button"
          class="chip"
          :class="{ 'chip-active': tenant_filter === t.value }"
          :title="t.label + ' (' + t.count + ')'"
          v-on:click.prevent="toggle_tenant_chip(t.value)"
        >{{ t.label }} ({{ t.count }})</button>
      </template>
      <template v-if="topUsers.length">
        <span class="chip-group-label">Users:</span>
        <button
          v-for="u in topUsers" :key="'user-' + u.value"
          type="button"
          class="chip"
          :class="{ 'chip-active': updated_by_filter === u.value }"
          :title="u.label + ' (' + u.count + ')'"
          v-on:click.prevent="toggle_user_chip(u.value)"
        >{{ u.label }} ({{ u.count }})</button>
      </template>
      <template v-if="topFields.length">
        <span class="chip-group-label">Fields:</span>
        <button
          v-for="f in topFields" :key="'field-' + f.value"
          type="button"
          class="chip"
          :class="{ 'chip-active': field_filter === f.value }"
          :title="f.label + ' (' + f.count + ')'"
          v-on:click.prevent="toggle_field_chip(f.value)"
        >{{ f.label }} ({{ f.count }})</button>
      </template>
    </div>

    <div style="margin:20px">
      <b-table :busy="loading" sticky-header="70vh" bordered :items="items" :fields="fields"
               :current-page="currentPage" :per-page="perPage">

        <template #table-busy>
          <div class="text-center text-danger my-2">
            <b-spinner class="align-middle"></b-spinner>
          </div>
        </template>

        <template slot="tenant_name" slot-scope="row">
          {{ row.item.tenant_name }}
        </template>

        <template slot="processed_time" slot-scope="row">
          {{row.item.processed_time | formatDateTimeSmall(row.item.timezone)}}
        </template>

        <template slot="updated_by" slot-scope="row">
          <div>{{ format_updated_by(row.item.updated_by) }}</div>
          <div v-if="display_name_differs(row.item.updated_by)" class="updated-by-username">
            {{ row.item.updated_by }}
          </div>
        </template>

        <template slot="diff" slot-scope="row">
          <settings-history-diff-cell :item="row.item" />
        </template>

      </b-table>
      <b-row>
        <b-col>
          <b-pagination
              v-model="currentPage"
              :total-rows="items.length"
              :per-page="perPage"
              align="center"
          >
          </b-pagination>
        </b-col>
      </b-row>
    </div>

  </div>
</template>

<script>

import {get_all_tenant_config_history, get_all_users} from '../networking/apis'
import {mapState} from "vuex";
import moment from 'moment'
import {justDate} from "@/helpers/dates";
import settings_history_diff_cell from './settings_history_diff_cell.vue'
import {get_changes, field_root, humanize_path} from '@/helpers/settings_history_diff'

const TOP_N = 3

export default {
  name: 'admin_settings_history',
  components: {
    'settings-history-diff-cell': settings_history_diff_cell,
  },

  data() {
    return {
      items: [],
      items_full: [],
      loading: true,
      tenant_filter: '',
      tenant_type_filter: '',
      telematics_type_filter: '',
      updated_by_filter: '',
      field_filter: '',
      filter_mode: 'quick',
      quick_range: 'week',
      quickRangeOptions: [
        { value: 'hour', label: 'Last Hour' },
        { value: 'day', label: 'Last Day' },
        { value: 'week', label: 'Last Week' },
        { value: 'month', label: 'Last Month' },
      ],
      from_date: justDate(moment().subtract(7, 'days')),
      to_date: justDate(moment()),
      users_by_username: {},
      currentPage: 1,
      perPage: 200,
      fields: [
        { key: 'tenant_name', label: 'Tenant', sortable: true, thStyle: { width: "160px" }},
        { key: 'processed_time', label: 'When', sortable: true, thStyle: { width: "150px" }},
        { key: 'updated_by', label: 'Updated by', sortable: true, thStyle: { width: "180px" }},
        { key: 'diff', label: 'Changes', sortable: false},
      ],
      dateOptions: {
        format: 'MM-DD-YYYY',
        useCurrent: true,
        minDate: moment().subtract(360, 'days'),
        maxDate: moment().add(0, 'days'),
        keepOpen: false,
        showClear: true,
        showClose: true,
        toolbarPlacement: 'top',
      },
    }
  },

  async created() {
    await this.load_data()
  },
  methods: {
    quick_range_bounds(range) {
      const unit_map = { hour: [1, 'hours'], day: [1, 'days'], week: [7, 'days'], month: [1, 'months'] }
      const [amount, unit] = unit_map[range] || unit_map.week
      return { since: moment().subtract(amount, unit).toISOString(), until: null }
    },
    explicit_range_bounds() {
      return {
        since: moment(this.from_date, 'MM-DD-YYYY').startOf('day').toISOString(),
        until: moment(this.to_date, 'MM-DD-YYYY').endOf('day').toISOString(),
      }
    },
    async apply_quick_range(range) {
      this.quick_range = range
      this.filter_mode = 'quick'
      await this.load_data()
    },
    async apply_explicit_range() {
      this.filter_mode = 'explicit'
      await this.load_data()
    },
    async load_data() {
      this.loading = true
      try {
        const { since, until } = this.filter_mode === 'explicit'
          ? this.explicit_range_bounds()
          : this.quick_range_bounds(this.quick_range)
        const history_promise = get_all_tenant_config_history(since, until)
        const users_promise = get_all_users().catch(() => null)

        const [history_resp, users_resp] = await Promise.all([history_promise, users_promise])
        this.users_by_username = this.build_users_map(users_resp)

        const {value: { data = {} } } = history_resp
        const records = data.records || []
        this.items_full = records.map(record => this.normalize_history_record(record))
        this.filter_results()
      } catch(e) {
        this.$emit('showAlert', 'error retrieving data, try logging in again or contact support if needed', 'danger', 5000)
      }
      this.loading = false
    },
    normalize_history_record(record) {
      if (!record) return record
      const normalized = { ...record }
      if (normalized.diff != null && typeof normalized.diff !== 'string') {
        try {
          normalized.diff = JSON.stringify(normalized.diff)
        } catch (e) {
          normalized.diff = String(normalized.diff)
        }
      }
      if (normalized.htmldiff != null && typeof normalized.htmldiff !== 'string') {
        try {
          normalized.htmldiff = JSON.stringify(normalized.htmldiff)
        } catch (e) {
          normalized.htmldiff = String(normalized.htmldiff)
        }
      }
      // precompute once for the top-fields chips and the field_filter — get_changes() parses
      // jsondiff/old-new snapshots, no need to redo that work on every filter/render pass
      normalized._changes = get_changes(normalized)
      return normalized
    },
    build_users_map(users_resp) {
      const map = {}
      try {
        const results = users_resp && users_resp.value && users_resp.value.data
          ? (users_resp.value.data.results || [])
          : []
        for (const u of results) {
          if (u && u.username) {
            map[u.username.toLowerCase()] = u
          }
        }
      } catch (e) {
        // best-effort lookup; fall back to username if anything goes wrong
      }
      return map
    },
    format_updated_by(username) {
      if (!username) return ''
      const user = this.users_by_username[String(username).toLowerCase()]
      if (!user) return username
      const first = (user.first_name || '').trim()
      const last = (user.last_name || '').trim()
      const full = [first, last].filter(Boolean).join(' ')
      return full || username
    },
    display_name_differs(username) {
      if (!username) return false
      const formatted = this.format_updated_by(username)
      return formatted && formatted !== username
    },
    filter_results() {
      this.items = this.items_full
      if (this.tenant_filter != '')
        this.items = this.items.filter(item => item.tenant_id === this.tenant_filter)
      if (this.tenant_type_filter != '')
        this.items = this.items.filter(item => item.tenant_type === this.tenant_type_filter)
      if (this.telematics_type_filter != '')
        this.items = this.items.filter(item => item.telematics_type === this.telematics_type_filter)
      if (this.updated_by_filter != '')
        this.items = this.items.filter(item => item.updated_by === this.updated_by_filter)
      if (this.field_filter != '')
        this.items = this.items.filter(item => (item._changes || []).some(c => field_root(c.path) === this.field_filter))
    },
    top_n_counts(items, keyFn, labelFn) {
      const counts = {}
      const labels = {}
      for (const item of items) {
        const key = keyFn(item)
        if (!key) continue
        counts[key] = (counts[key] || 0) + 1
        if (!labels[key]) labels[key] = labelFn(item)
      }
      return Object.entries(counts)
        .sort((a, b) => b[1] - a[1])
        .slice(0, TOP_N)
        .map(([key, count]) => ({ value: key, label: labels[key], count }))
    },
    toggle_tenant_chip(value) {
      this.tenant_filter = this.tenant_filter === value ? '' : value
    },
    toggle_user_chip(value) {
      this.updated_by_filter = this.updated_by_filter === value ? '' : value
    },
    toggle_field_chip(value) {
      this.field_filter = this.field_filter === value ? '' : value
    },
  },
  computed:{
    ...mapState({
      user: state => state.profile.user
    }),
    tenantOptions() {
      const options = (this.user.tenant_options || []).slice().sort((a, b) => a.text.localeCompare(b.text))
      return [{ value: '', text: 'All Tenants' }, ...options]
    },
    tenantTypeOptions() {
      const types = this.user.types || {}
      const unique = [...new Set(Object.values(types).filter(Boolean))].sort()
      return [{ value: '', text: 'All Tenant Types' }, ...unique.map(t => ({ value: t, text: t }))]
    },
    telematicsTypeOptions() {
      const types = this.user.telematics_types || {}
      const unique = [...new Set(Object.values(types).filter(Boolean))].sort()
      return [{ value: '', text: 'All Telematics Types' }, ...unique.map(t => ({ value: t, text: t }))]
    },
    // top-N chips reflect the full loaded date-range window (items_full), not the
    // currently-filtered `items` — so they stay meaningful reference points rather than
    // collapsing to trivial single entries once you've already filtered by that facet
    topTenants() {
      return this.top_n_counts(this.items_full, item => item.tenant_id, item => item.tenant_name)
    },
    topUsers() {
      return this.top_n_counts(this.items_full, item => item.updated_by, item => this.format_updated_by(item.updated_by))
    },
    topFields() {
      const counts = {}
      for (const item of this.items_full) {
        const roots = new Set((item._changes || []).map(c => field_root(c.path)))
        for (const root of roots) {
          counts[root] = (counts[root] || 0) + 1
        }
      }
      return Object.entries(counts)
        .sort((a, b) => b[1] - a[1])
        .slice(0, TOP_N)
        .map(([root, count]) => ({ value: root, label: humanize_path(root), count }))
    },
  },
  watch: {
    tenant_filter() {
      this.filter_results()
    },
    tenant_type_filter() {
      this.filter_results()
    },
    telematics_type_filter() {
      this.filter_results()
    },
    updated_by_filter() {
      this.filter_results()
    },
    field_filter() {
      this.filter_results()
    },
  }
}
</script>

<style scoped>

  .settings-history-filters-row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: center;
    gap: 10px;
    margin: 15px 10px 10px;
  }

  .filters-divider {
    width: 1px;
    align-self: stretch;
    background: #d0d8e4;
    margin: 0 2px;
  }

  .admin-users-count {
    font-size: 14px;
    font-weight: 600;
    color: #555;
  }

  .admin-users-select {
    width: auto;
    min-width: 150px;
  }

  .admin-users-range-sep {
    font-size: 13px;
    color: #888;
    font-style: italic;
  }

  .admin-users-explicit-range {
    font-size: 14px;
    display: flex;
    align-items: center;
  }

  .updated-by-username {
    font-size: 11px;
    color: #888;
    margin-top: 2px;
    word-break: break-all;
  }

  .settings-history-chips-row {
    display: flex;
    flex-wrap: nowrap;
    align-items: center;
    justify-content: center;
    gap: 6px;
    margin: 0 10px 15px;
    overflow-x: auto;
    padding-bottom: 4px;
  }

  .chip-group-label {
    font-size: 12px;
    font-weight: 600;
    color: #666;
    margin-right: 2px;
    margin-left: 10px;
    flex-shrink: 0;
  }

  .chip-group-label:first-child {
    margin-left: 0;
  }

  .chip {
    border: 1px solid steelblue;
    background: rgba(70, 130, 180, 0.08);
    color: steelblue;
    border-radius: 12px;
    padding: 2px 8px;
    font-size: 11px;
    cursor: pointer;
    transition: background 0.12s, border-color 0.12s;
    max-width: 170px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex-shrink: 0;
  }

  .chip:hover {
    background: rgba(70, 130, 180, 0.18);
  }

  .chip-active {
    background: steelblue;
    border-color: steelblue;
    color: white;
  }

</style>
